import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { searchArticles, getCategoryName } from '@/lib/content';
import type { Article } from '@/types/content';

interface SearchOverlayProps {
  onClose: () => void;
}

export default function SearchOverlay({ onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const results = useMemo(() => searchArticles(keyword).slice(0, 6), [keyword]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const go = (id: string) => {
    onClose();
    navigate(`/article/${id}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-foreground-950/40 px-4 pt-24"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-background-200 bg-background-50 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-background-200 pb-3">
          <i className="ri-search-line text-xl text-foreground-500" aria-hidden="true"></i>
          <input
            autoFocus
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索沙雕新闻、迷惑行为、奇葩人物…"
            className="w-full bg-transparent text-base text-foreground-950 outline-none placeholder:text-foreground-400"
            aria-label="搜索内容"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-lg text-foreground-500 hover:bg-background-100"
            aria-label="关闭搜索"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        </div>

        <div className="mt-3 max-h-[50vh] overflow-y-auto">
          {results.length === 0 ? (
            <p className="py-8 text-center text-sm text-foreground-500">
              暂时没搜到这个瓜，换个关键词试试？
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {results.map((item: Article) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => go(item.id)}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className="ri-fire-line" aria-hidden="true"></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="line-clamp-1 text-sm font-medium text-foreground-900">
                        {item.title}
                      </span>
                      <span className="text-xs text-foreground-500">{getCategoryName(item.category)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="mt-3 text-center text-xs text-foreground-400">按 Esc 快速关闭</p>
      </div>
    </div>
  );
}